import React, { useEffect, useState, useMemo } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Typography,
  Paper,
  TableContainer,
  CircularProgress,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  IconButton,
  Chip,
  TableSortLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatAmount } from '../../../utils/formatAmount';
import useReport from '../useReport';

const normalizeShop = (s) => String(s || '').toLowerCase().trim();

const shopChipSx = (shop) => {
  const key = normalizeShop(shop);
  if (key === 'tama') return { bgcolor: '#ca8a04', color: '#fff', fontWeight: 700 };
  if (key === 'smile') return { bgcolor: '#4338ca', color: '#fff', fontWeight: 700 };
  return { bgcolor: '#64748b', color: '#fff', fontWeight: 600 };
};

const UnpaidDetailsModal = ({ month, formattedMonth }) => {
  const [allDetails, setAllDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shopFilter, setShopFilter] = useState('all');
  const [timeScope, setTimeScope] = useState('month');
  const [showCalcInfo, setShowCalcInfo] = useState(false);
  const [sortKey, setSortKey] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const { getUnpaidDetailReport } = useReport();
  const allTime = timeScope === 'all';

  useEffect(() => {
    if (!allTime && !formattedMonth) return;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const payload = await getUnpaidDetailReport(formattedMonth, 'all', allTime);
        const inner = payload?.data ?? payload;
        setAllDetails(inner?.details || []);
      } catch (error) {
        console.error('Error fetching unpaid details:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [formattedMonth, allTime]);

  const shopOptions = useMemo(() => {
    const shops = new Set(allDetails.map(d => d.shop));
    return ['all', ...Array.from(shops).sort()];
  }, [allDetails]);

  const filteredDetails = useMemo(() => {
    const rows = shopFilter === 'all'
      ? allDetails
      : allDetails.filter(d => d.shop === shopFilter);
    const getVal = (row) => {
      switch (sortKey) {
        case 'shop':
          return normalizeShop(row.shop);
        case 'contact':
          return String(row.contact || '').toLowerCase();
        case 'smileName':
          return String(row.smileName || '').toLowerCase();
        case 'orders':
          return Number(row.totalOrders || 0);
        case 'amount':
          return Number(row.payments?.unpaid?.amount || 0);
        case 'delivery':
          return Number(row.payments?.unpaid?.delivery || 0);
        case 'weight':
          return Number(row.payments?.unpaid?.weight || 0);
        case 'thaiDelivery':
          return Number(row.payments?.unpaid?.thaiDelivery || 0);
        case 'transaction':
          return Number(row.payments?.unpaid?.transaction || 0);
        case 'downRemaining':
          return Number(row.payments?.downPaymentRemaining || 0);
        case 'slowPaid':
          return Number(row.payments?.slowPaid || 0);
        case 'total':
        default:
          return Number(row.total || 0);
      }
    };

    const dirMul = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av).localeCompare(String(bv)) * dirMul;
      }
      return (Number(av) - Number(bv)) * dirMul;
    });
  }, [allDetails, shopFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    setSortKey((prevKey) => {
      if (prevKey !== key) {
        setSortDir('desc');
        return key;
      }
      setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
      return prevKey;
    });
  };

  const SortHeader = ({ id, label, align = 'right', disabled = false }) => (
    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align={align}>
      {disabled ? (
        label
      ) : (
        <TableSortLabel
          active={sortKey === id}
          direction={sortKey === id ? sortDir : 'asc'}
          onClick={() => toggleSort(id)}
          sx={{
            color: '#ffffff',
            '&.Mui-active': { color: '#ffffff' },
            '&.MuiTableSortLabel-root:hover': { color: '#ffffff' },
            '& .MuiTableSortLabel-icon': { color: '#ffffff !important' },
          }}
        >
          {label}
        </TableSortLabel>
      )}
    </TableCell>
  );

  const filteredSummary = useMemo(() => {
    const rows = shopFilter === 'all' ? allDetails : allDetails.filter(d => d.shop === shopFilter);
    return rows.reduce((acc, curr) => ({
      totalOrders: acc.totalOrders + curr.totalOrders,
      payments: {
        downPaymentRemaining: acc.payments.downPaymentRemaining + curr.payments.downPaymentRemaining,
        slowPaid: acc.payments.slowPaid + curr.payments.slowPaid,
        unpaid: {
          amount: acc.payments.unpaid.amount + curr.payments.unpaid.amount,
          delivery: acc.payments.unpaid.delivery + curr.payments.unpaid.delivery,
          weight: acc.payments.unpaid.weight + curr.payments.unpaid.weight,
          thaiDelivery: acc.payments.unpaid.thaiDelivery + curr.payments.unpaid.thaiDelivery,
          transaction: acc.payments.unpaid.transaction + curr.payments.unpaid.transaction,
        },
      },
      total: acc.total + curr.total,
    }), {
      totalOrders: 0,
      payments: { downPaymentRemaining: 0, slowPaid: 0, unpaid: { amount: 0, delivery: 0, weight: 0, thaiDelivery: 0, transaction: 0 } },
      total: 0,
    });
  }, [allDetails, shopFilter]);

  /** Open Customer Profile in a new tab, using the same shop switching mechanism as Sidebar (localStorage). */
  const handleContactClick = (smileName, rowShop) => {
    if (!smileName) return;
    const targetShop = normalizeShop(rowShop);
    if (!targetShop) return;
    // Do NOT redirect the current tab. Set shop for the new tab via localStorage.
    localStorage.setItem('shopName', targetShop);
    window.open(`/customer-profile?smileName=${encodeURIComponent(smileName)}`, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" gutterBottom>
        Unpaid Details — {allTime ? 'All time' : month}
      </Typography>
      {allTime && month ? (
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
          Lifetime unpaid / slow paid across all order dates (opened from row {month}).
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <ToggleButtonGroup
          value={timeScope}
          exclusive
          onChange={(e, val) => { if (val !== null) setTimeScope(val); }}
          size="small"
        >
          <ToggleButton value="month">This month</ToggleButton>
          <ToggleButton value="all">All time</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={shopFilter}
          exclusive
          onChange={(e, val) => { if (val !== null) setShopFilter(val); }}
          size="small"
        >
          {shopOptions.map(s => (
            <ToggleButton key={s} value={s}>
              {s === 'all' ? 'All Shops' : s}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          {filteredDetails.length} customers
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1565c0' }}>
              <SortHeader id="shop" label="Shop" align="left" />
              <SortHeader id="contact" label="Customer" align="left" />
              <SortHeader id="smileName" label="Smile Name" align="left" />
              <SortHeader id="orders" label="Orders" />
              <SortHeader id="amount" label="Amount" />
              <SortHeader id="delivery" label="Delivery" />
              <SortHeader id="weight" label="Weight" />
              <SortHeader id="thaiDelivery" label="Thai Delivery" />
              <SortHeader id="transaction" label="Transaction" />
              <SortHeader id="downRemaining" label="Down Remaining" />
              <SortHeader id="total" label="Total" />
              <SortHeader id="slowPaid" label="Slow Paid" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDetails.map((detail, index) => (
              <TableRow key={`${detail.shop}-${detail.smileName}-${index}`} hover>
                <TableCell sx={{ width: 90, maxWidth: 90, verticalAlign: 'top' }}>
                  <Chip
                    label={normalizeShop(detail.shop) || detail.shop}
                    size="small"
                    sx={{ ...shopChipSx(detail.shop), textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => handleContactClick(detail.smileName, detail.shop)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleContactClick(detail.smileName, detail.shop);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover .unpaid-contact-name': { textDecoration: 'underline' },
                      display: 'inline-block',
                      maxWidth: 220,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={`${detail.isBlacklisted ? '❌ ' : ''}${detail.isVip ? '⭐ ' : ''}${detail.contact || ''}`}
                  >
                    <Typography className="unpaid-contact-name" variant="body2" sx={{ color: '#1565c0', fontWeight: 600, textAlign: 'left' }}>
                      {detail.isBlacklisted ? '❌ ' : ''}{detail.isVip ? '⭐ ' : ''}{detail.contact}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{detail.smileName}</TableCell>
                <TableCell align="right">{detail.totalOrders}</TableCell>
                <TableCell align="right">{formatAmount(detail.payments.unpaid.amount)}</TableCell>
                <TableCell align="right">{formatAmount(detail.payments.unpaid.delivery)}</TableCell>
                <TableCell align="right">{formatAmount(detail.payments.unpaid.weight)}</TableCell>
                <TableCell align="right">{formatAmount(detail.payments.unpaid.thaiDelivery)}</TableCell>
                <TableCell align="right">{formatAmount(detail.payments.unpaid.transaction)}</TableCell>
                <TableCell align="right">{formatAmount(detail.payments.downPaymentRemaining)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatAmount(detail.total)}
                </TableCell>
                <TableCell align="right" sx={{ color: '#b91c1c', fontWeight: 600 }}>
                  {formatAmount(detail.payments.slowPaid)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: '#e7e5e4' }}>
              <TableCell colSpan={3} sx={{ fontWeight: 700, color: '#0c0a09' }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{filteredSummary.totalOrders}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{formatAmount(filteredSummary.payments.unpaid.amount)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{formatAmount(filteredSummary.payments.unpaid.delivery)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{formatAmount(filteredSummary.payments.unpaid.weight)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{formatAmount(filteredSummary.payments.unpaid.thaiDelivery)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{formatAmount(filteredSummary.payments.unpaid.transaction)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{formatAmount(filteredSummary.payments.downPaymentRemaining)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0c0a09' }}>{formatAmount(filteredSummary.total)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#b91c1c' }}>{formatAmount(filteredSummary.payments.slowPaid)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Calculation summary */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9fafb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowCalcInfo(v => !v)}>
          <IconButton size="small" sx={{ mr: 0.5 }}>
            {showCalcInfo ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>
            How is this calculated?
          </Typography>
        </Box>
        <Collapse in={showCalcInfo} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 2, pt: 1 }}>
            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>
              {allTime ? (
                <>Orders across <strong>all dates</strong> that are not cancelled (&quot;Cancel by store&quot;), not deleted, and have at least one unpaid line.</>
              ) : (
                <>Orders in <strong>{month}</strong> that are not cancelled (&quot;Cancel by store&quot;), not deleted, and have at least one unpaid line.</>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>
              <strong>Slow Paid:</strong> No ship date → product counts only if still <strong>fully unpaid</strong> (มัดจำ 50% / ชำระครบ → 0 here); JP/weight/Thai stay 0 until ship + arrival. With ship date → product &amp; JP only when <strong>arrived at least 7 days ago</strong> (<code>thaiDate</code> ≤ today − 7 days). <strong>Transaction fee</strong>: use <code>transactionFeePaid</code> only — unpaid fee counts when there is <strong>no ship</strong> or when <strong>arrived ≥7 days</strong>; 0 while ship set but not yet 7 days after arrival. Weight &amp; Thai: arrived ≥7 days &amp; unpaid. Main columns unchanged.
            </Typography>
            <Table size="small" sx={{ maxWidth: 720, mb: 1 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600, width: 160 }}>Amount</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>THB = amount × qty × productRate. &quot;รอชำระ&quot;: full. &quot;มัดจำ 50%&quot; / &quot;มัดจำ 50% แล้ว&quot;: second half (×0.5). No arrival check.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>Delivery</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>Same as Amount for JP delivery (deliveryFeeJp × codRate or productRate). No arrival check.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>Weight</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>If weight not paid: weightPrice or weight × weightRate. No arrival check.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>Thai Delivery</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>If shipThPaid is not true: deliveryFeeTh. No arrival check.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>Transaction</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>transactionFee only if fee &gt; 0 and transactionFeePaid is not true.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>Down Remaining</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>Reference: 50% of product and JP delivery (× qty × rates) for down-payment statuses, regardless of arrival.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>Sum of Amount + Delivery + Weight + Thai Delivery + Transaction columns above (current-due view).</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>Slow Paid</TableCell>
                  <TableCell sx={{ border: 0, color: '#4b5563' }}>No ship → full unpaid product only (deposit rows 0); JP 0 until arrived ≥7 days. With ship: product/JP/weight/Thai/transaction (when ship exists) only if <code>thaiDate</code> is at least 7 days before today. Transaction with no ship: unpaid fee as before.</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
              Grouped per customer per shop. Click a customer name to switch the app shop (same as Sidebar &quot;Switch Shop&quot;) and open Customer Profile in that shop.
            </Typography>
          </Box>
        </Collapse>
      </Paper>
    </div>
  );
};

export default UnpaidDetailsModal;
